class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        
        // ADD: Apply badge management after content is rendered
        setTimeout(() => {
          FacetFiltersForm.manageBadgesForFiltering();
        }, 100);
        
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    
    // ADD: Apply badge management after content is rendered
    setTimeout(() => {
      FacetFiltersForm.manageBadgesForFiltering();
    }, 100);
    
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newElementSelector = document
          .getElementById(closestJSFilterID)
          .classList.contains('mobile-facets__details')
          ? `#${closestJSFilterID} .mobile-facets__close-button`
          : `#${closestJSFilterID} .facets__summary`;
        const newElementToActivate = document.querySelector(newElementSelector);
        if (newElementToActivate) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  // ADD: Badge management methods
  static getActiveSizes() {
    const urlParams = new URLSearchParams(window.location.search);
    const sizes = [];

    for (const [key, value] of urlParams.entries()) {
      if (key.includes('filter.v.option.talla') || key.includes('filter.v.option.size')) {
        const decodedValues = decodeURIComponent(value).split(',');
        sizes.push(...decodedValues.map(size => size.trim()));
      }
    }

    return sizes.length > 0 ? sizes : null;
  }

  static manageBadgesForFiltering() {
    const activeSizes = FacetFiltersForm.getActiveSizes();
    const productCards = document.querySelectorAll('.card-wrapper.product-card-wrapper');
    
    productCards.forEach(card => {
      const shouldShowSoldOutBadge = FacetFiltersForm.checkIfSoldOut(card, activeSizes);
      FacetFiltersForm.updateProductBadge(card, shouldShowSoldOutBadge);
    });
  }

  static checkIfSoldOut(productCard, activeSizes) {
    const variantOptions = productCard.querySelectorAll('[data-variant-size], .variant-option');

    if (variantOptions.length === 0) {
      // No variant data found, fallback to global sold out badge
      const soldOutBadge = productCard.querySelector('.badge');
      if (soldOutBadge && soldOutBadge.textContent.trim().toLowerCase().includes('sold out')) {
        return true;
      }
      return false;
    }

    // If no sizes selected, check if all variants are sold out
    if (!activeSizes || activeSizes.length === 0) {
      const availableVariants = Array.from(variantOptions).filter(el => 
        el.getAttribute('data-variant-available') === 'true'
      );
      return availableVariants.length === 0;
    }

    // Check if any selected size is available
    for (const variantEl of variantOptions) {
      const size = variantEl.getAttribute('data-variant-size')?.trim();
      const available = variantEl.getAttribute('data-variant-available') === 'true';

      if (size && available && activeSizes.includes(size)) {
        return false; // Found a matching size that is available
      }
    }

    // If no available matching size found
    return true;
  }

  // Helper function to get variant strings
  static getVariantString(key, fallback) {
    if (window.variantStrings && window.variantStrings[key]) {
      return window.variantStrings[key];
    }
    return fallback;
  }

  static updateProductBadge(productCard, shouldShowSoldOut) {
    // Find all badge containers in this product card
    const badgeContainers = productCard.querySelectorAll('.card__badge');
    
    badgeContainers.forEach(container => {
      // Find existing badges
      let soldOutBadge = container.querySelector('.badge');
      const isSoldOutBadge = soldOutBadge && soldOutBadge.textContent.toLowerCase().includes('sold out');
      
      if (shouldShowSoldOut) {
        // Need to show sold out badge
        if (!isSoldOutBadge) {
          // Hide any existing sale/discount badges
          const existingBadges = container.querySelectorAll('.badge');
          existingBadges.forEach(badge => {
            if (!badge.textContent.toLowerCase().includes('sold out')) {
              badge.style.display = 'none';
            }
          });
          
          // Create new sold out badge if none exists
          if (!soldOutBadge) {
            // Get the sold out badge color scheme from theme settings
            const existingBadge = document.querySelector('.badge[class*="color-a"]');
            let colorScheme = 'accent-2'; // fallback
            
            if (existingBadge) {
              const colorMatch = existingBadge.className.match(/color-[\w-]+/);
              if (colorMatch) {
                colorScheme = colorMatch[0]; // This will be like "color-inverse"
              }
            }
            
            soldOutBadge = document.createElement('span');
            // Use your theme's exact badge structure and color scheme
            soldOutBadge.className = `badge badge--bottom-left ${colorScheme}`;
            container.appendChild(soldOutBadge);
          }
          
          // Get the sold out text from window.variantStrings
          const soldOutText = FacetFiltersForm.getVariantString('soldOut', 'Sold out');
          
          soldOutBadge.textContent = soldOutText;
          soldOutBadge.style.display = 'block';
          soldOutBadge.setAttribute('data-js-managed', 'true');
        } else {
          // Already showing sold out badge, ensure it's visible
          soldOutBadge.style.display = 'block';
        }
      } else {
        // Should NOT show sold out badge
        if (isSoldOutBadge && soldOutBadge.hasAttribute('data-js-managed')) {
          // Hide JavaScript-managed sold out badge
          soldOutBadge.style.display = 'none';
        } else if (isSoldOutBadge) {
          // This is a natural sold out badge - check if we should hide it
          const hasAnyAvailableVariant = FacetFiltersForm.hasAvailableVariants(productCard);
          if (hasAnyAvailableVariant) {
            soldOutBadge.style.display = 'none';
          }
        }
        
        // Show any hidden sale/discount badges
        const hiddenBadges = container.querySelectorAll('.badge[style*="display: none"]');
        hiddenBadges.forEach(badge => {
          if (badge.textContent.includes('%') || badge.textContent.toLowerCase().includes('sale')) {
            badge.style.display = 'block';
          }
        });
      }
    });
  }

  static hasAvailableVariants(productCard) {
    const variantOptions = productCard.querySelectorAll('[data-variant-available]');
    return Array.from(variantOptions).some(variant => 
      variant.getAttribute('data-variant-available') === 'true'
    );
  }

  static resetAllBadges() {
    const productCards = document.querySelectorAll('.card-wrapper.product-card-wrapper');
    
    productCards.forEach(card => {
      const badgeContainers = card.querySelectorAll('.card__badge');
      
      badgeContainers.forEach(container => {
        // Remove JavaScript-created badges
        const jsCreatedBadges = container.querySelectorAll('[data-js-managed="true"]');
        jsCreatedBadges.forEach(badge => {
          if (badge.textContent.toLowerCase().includes('sold out')) {
            badge.remove();
          }
        });
        
        // Show all original badges
        const originalBadges = container.querySelectorAll('.badge');
        originalBadges.forEach(badge => {
          badge.style.display = '';
        });
      });
    });
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// ADD: Initialize badge management on page load
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(() => {
    FacetFiltersForm.manageBadgesForFiltering();
  }, 500);
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);